import { useEffect, useMemo, useState } from 'react'
import { listBuurten } from '../../helpers/pdok/fetchPdok'

// De namen van de buurten in de gekozen wijken van een wervingsactie, op
// buurtcode. Adressen en registratierijen dragen alleen de code.
//
// Nodig omdat zowel 'Gebied' (AddressSelect) als 'Registratie' de namen
// tonen; zo wordt er één keer per wijk opgehaald, in Detail.jsx, in plaats van
// per tabblad. Mislukt PDOK, dan blijft het object leeg en tonen de schermen
// de code: dat is waar, alleen minder leesbaar. De fout staat in de console.
// De laadstand gaat mee, zodat een download niet stil codes wegschrijft
// terwijl de namen nog onderweg zijn.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10)

/**
 * @param {Array<{ WijkCode: string }>} wijken - De gekozen wijken van de actie
 * @returns {{ names: Object<string, string>, loadState: 'idle' | 'loading' | 'complete' | 'failed' }} Buurtcode → buurtnaam, en hoe ver het laden is
 */
const useBuurtNames = (wijken) => {
    const [names, setNames] = useState(/** @type {Object<string, string>} */ ({}))
    const [loadState, setLoadState] = useState(/** @type {'idle' | 'loading' | 'complete' | 'failed'} */ ('idle'))

    // Een vaste sleutel, zodat een nieuwe array met dezelfde wijken (elke
    // Firestore-snapshot) niet opnieuw ophaalt.
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const wijkKey = useMemo(() => (wijken || []).map((w) => w.WijkCode).sort().join('|'), [wijken])

    useEffect(() => {
        const codes = wijkKey ? wijkKey.split('|') : []
        if (codes.length === 0) {
            setNames({})
            setLoadState('idle')
            return undefined
        }
        setLoadState('loading')

        const controller = new AbortController()
        let cancelled = false
        Promise.all(codes.map((code) => listBuurten(code, controller.signal)))
            .then((lists) => {
                if (cancelled) return
                setNames(Object.fromEntries(lists.flat().map((b) => [b.BuurtCode, b.Naam])))
                setLoadState('complete')
            })
            .catch((err) => {
                if (cancelled || err.name === 'AbortError') return
                console.error('PDOK buurt list failed', err)
                setLoadState('failed')
            })

        return () => {
            cancelled = true
            controller.abort()
        }
    }, [wijkKey])

    return { names, loadState }
}

export default useBuurtNames
